import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { getApiUrl } from '@/lib/config';
import { useCreateChannel } from '../../hooks';
import { useFacebookEmbeddedSignup } from '../../hooks/useFacebookEmbeddedSignup';
import { FacebookPage } from '../../services/facebookApi';
import { Icon } from '@/shared/icons';
import { TokenStatusAlert, type TokenInfo } from './TokenStatusAlert';

const META_GRAPH_API_VERSION = 'v23.0';
const META_GRAPH_API_BASE = `https://graph.facebook.com/${META_GRAPH_API_VERSION}`;

const REQUIRED_MESSENGER_SCOPES = [
  'pages_show_list',
  'pages_messaging',
  'pages_manage_metadata',
];

interface MetaPageOption {
  id: string;
  name: string;
  category?: string;
  pageAccessToken: string;
}

async function fetchTokenInfo(token: string, requiredScopes: string[]): Promise<TokenInfo | null> {
  try {
    const url = `${META_GRAPH_API_BASE}/debug_token?input_token=${encodeURIComponent(token)}&access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.error || !data.data) return null;
    const scopes: string[] = data.data.scopes || [];
    const missingScopes = requiredScopes.filter((s) => !scopes.includes(s));
    return {
      expiresAt: typeof data.data.expires_at === 'number' ? data.data.expires_at : 0,
      scopes,
      isValid: data.data.is_valid !== false,
      missingScopes,
    };
  } catch {
    return null;
  }
}
/**
 * Form for connecting Facebook Messenger via Meta Platform
 *
 * Flow:
 * - Embedded: Facebook Login → Select Page → Auto-configure → Show webhook URL
 * - Manual: Collect credentials → Test connection → Show webhook URL
 *
 * @example
 * <FacebookMessengerForm
 *   onComplete={(data) => handleAuthComplete(data)}
 * />
 */
export interface FacebookMessengerFormProps {
  /** Callback when authentication is complete */
  onComplete: (data?: FacebookMessengerData) => void;
}

export interface FacebookMessengerData {
  providerId: string;
  name: string;
  identifier: string;
  config?: {
    pageId?: string;
    webhookToken?: string;
    webhookConfigured?: boolean;
  };
  credentials: {
    accessToken: string;
    pageId: string;
  };
}

type ConnectionStep = 'credentials' | 'connecting' | 'page-selection' | 'webhook' | 'complete';
type AuthMode = 'embedded' | 'manual';

export const FacebookMessengerForm: React.FC<FacebookMessengerFormProps> = ({
  onComplete,
}) => {
  const [step, setStep] = useState<ConnectionStep>('credentials');
  const [authMode, setAuthMode] = useState<AuthMode>('embedded');
  const [channelName, setChannelName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [pageId, setPageId] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<FacebookPage | null>(null);

  const createChannel = useCreateChannel();

  // Facebook Embedded Signup hook
  const {
    startEmbeddedSignup,
    selectFacebookPage,
    reset: resetEmbedded,
    isLoading: isEmbeddedLoading,
    error: embeddedError,
    result: embeddedResult,
    pageSelectionRequired,
    availablePages,
  } = useFacebookEmbeddedSignup();

  // Manual mode — Graph API discovery state
  const [isConnectingToken, setIsConnectingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [pages, setPages] = useState<MetaPageOption[]>([]);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);

  // Handle step changes based on hook state (embedded mode)
  useEffect(() => {
    if (authMode !== 'embedded') return;

    if (isEmbeddedLoading) {
      setStep('connecting');
    } else if (embeddedError) {
      setStep('credentials');
      setErrorMessage(embeddedError);
    } else if (pageSelectionRequired && availablePages.length > 0) {
      setStep('page-selection');
    } else if (embeddedResult) {
      // Auto-create channel with embedded credentials
      handleEmbeddedChannelCreate(embeddedResult);
    }
  }, [isEmbeddedLoading, embeddedError, pageSelectionRequired, availablePages, embeddedResult, authMode]);

  const handleEmbeddedChannelCreate = async (result: typeof embeddedResult) => {
    if (!result) return;

    // Use default name or user-provided name
    const autoChannelName = channelName.trim() || 'Facebook Messenger';

    setStep('connecting');

    try {
      const channelRequest = {
        name: autoChannelName,
        identifier: result.pageId,
        providerId: 'facebook-messenger',
        config: {
          pageId: result.pageId,
          webhookToken: 'auto-generated',
          webhookConfigured: result.webhookConfigured,
        },
        credentials: {
          accessToken: result.accessToken,
          pageId: result.pageId,
        },
        integrationName: `${autoChannelName} Integration`,
        assignmentConfig: {
          enabled: true,
          type: 'sequential' as const,
        },
      };

      const channel = await createChannel.mutateAsync(channelRequest);

      // Build webhook URL com baseURL do backend (não do frontend)
      const integrationId = channel.integrationId;
      setWebhookUrl(`${getApiUrl()}/api/provider/webhook/${integrationId}`);

      // If webhook was already configured by backend, go to complete
      if (result.webhookConfigured) {
        setStep('complete');
      } else {
        setStep('webhook');
      }
    } catch (error: any) {
      if (error.response?.status === 403) return; // Handled by global interceptor (toast)
      if (error.response?.status === 402) return; // Handled by global interceptor (toast)
      console.error('❌ Erro ao criar canal via Embedded Signup:', error);
      setErrorMessage(error.response?.data?.message || 'Erro ao criar canal. Tente novamente.');
      setStep('credentials');
      resetEmbedded();
    }
  };

  // Handle connect button click (embedded mode)
  const handleEmbeddedConnect = async () => {
    setErrorMessage(null);
    await startEmbeddedSignup();
  };

  // Handle page selection (embedded mode)
  const handlePageSelect = async (page: FacebookPage) => {
    setSelectedPage(page);
    await selectFacebookPage(page);
  };

  const resetManualDiscovery = () => {
    setPages([]);
    setPageId('');
    setSelectedPage(null);
    setTokenError(null);
    setTokenInfo(null);
  };

  const handleConnectToken = async () => {
    if (!accessToken.trim()) {
      setTokenError('Cole seu Access Token primeiro');
      return;
    }

    setIsConnectingToken(true);
    setTokenError(null);
    resetManualDiscovery();

    try {
      const url = `${META_GRAPH_API_BASE}/me/accounts?fields=id,name,category,access_token&limit=100&access_token=${encodeURIComponent(accessToken.trim())}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.error) {
        setTokenError(data.error.message || 'Token inválido');
        return;
      }

      const pagesList: MetaPageOption[] = (data.data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        category: p.category,
        pageAccessToken: p.access_token,
      }));

      if (pagesList.length === 0) {
        setTokenError('Nenhuma página do Facebook encontrada. Verifique se o token tem a permissão pages_show_list.');
        return;
      }

      setPages(pagesList);

      // Buscar metadata do token (expiração + scopes)
      const info = await fetchTokenInfo(accessToken.trim(), REQUIRED_MESSENGER_SCOPES);
      setTokenInfo(info);

      if (pagesList.length === 1) {
        const only = pagesList[0]!;
        setPageId(only.id);
        setSelectedPage({ id: only.id, name: only.name, category: only.category });
        if (!channelName.trim()) {
          setChannelName(`Messenger ${only.name}`);
        }
      }
    } catch (err) {
      setTokenError(err instanceof Error ? err.message : 'Erro ao conectar com o Meta');
    } finally {
      setIsConnectingToken(false);
    }
  };

  const handleSelectPage = (newPageId: string) => {
    setPageId(newPageId);
    if (!newPageId) {
      setSelectedPage(null);
      return;
    }
    const page = pages.find((p) => p.id === newPageId);
    if (!page) return;
    setSelectedPage({ id: page.id, name: page.name, category: page.category });
    if (!channelName.trim()) {
      setChannelName(`Messenger ${page.name}`);
    }
  };

  // Handle manual connect
  const handleManualConnect = async () => {
    if (!accessToken.trim() || !pageId.trim()) {
      setErrorMessage('Selecione uma página para continuar');
      return;
    }

    const finalName = channelName.trim() || (selectedPage ? `Messenger ${selectedPage.name}` : 'Facebook Messenger');
    if (finalName.length < 2) {
      setErrorMessage('Nome do canal deve ter pelo menos 2 caracteres');
      return;
    }

    setErrorMessage(null);
    setStep('connecting');

    try {
      const channelRequest = {
        name: finalName,
        identifier: pageId,
        providerId: 'facebook-messenger',
        config: {
          pageId,
          webhookToken: 'manual',
          webhookConfigured: false,
        },
        credentials: {
          accessToken,
          pageId,
        },
        integrationName: `${finalName} Integration`,
        assignmentConfig: {
          enabled: true,
          type: 'sequential' as const,
        },
      };

      const channel = await createChannel.mutateAsync(channelRequest);

      // Build webhook URL com baseURL do backend (não do frontend)
      const integrationId = channel.integrationId;
      setWebhookUrl(`${getApiUrl()}/api/provider/webhook/${integrationId}`);

      setStep('webhook');
    } catch (error: any) {
      if (error.response?.status === 403) return; // Handled by global interceptor (toast)
      if (error.response?.status === 402) return; // Handled by global interceptor (toast)
      console.error('❌ Erro ao criar canal manual:', error);
      setErrorMessage(error.response?.data?.message || 'Erro ao criar canal. Tente novamente.');
      setStep('credentials');
    }
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
  };

  const handleComplete = () => {
    // Canal já criado em handleManualConnect / handleEmbeddedChannelCreate — apenas fecha
    onComplete();
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Credentials Input */}
      {step === 'credentials' && (
        <div className="space-y-4">
          {/* Auth Mode Selector */}
          <div
            className="flex rounded-lg overflow-hidden border"
            style={{ borderColor: 'var(--theme-card-border)' }}
          >
            <button
              type="button"
              onClick={() => setAuthMode('embedded')}
              className="flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              style={{
                backgroundColor: authMode === 'embedded' ? 'var(--theme-button-primary)' : 'transparent',
                color: authMode === 'embedded' ? 'white' : 'var(--theme-card-text)',
              }}
            >
              <Icon name="facebook" size={18} />
              Login com Facebook
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('manual')}
              className="flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              style={{
                backgroundColor: authMode === 'manual' ? 'var(--theme-button-primary)' : 'transparent',
                color: authMode === 'manual' ? 'white' : 'var(--theme-card-text)',
              }}
            >
              <Icon name="key" size={18} />
              Credenciais Manuais
            </button>
          </div>

          {/* ============================================= */}
          {/* EMBEDDED MODE - Login com Facebook           */}
          {/* ============================================= */}
          {authMode === 'embedded' && (
            <div className="space-y-4">
              {/* Channel Name (optional for embedded) */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Nome do Canal (opcional)
                </label>
                <Input
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="Ex: Facebook Messenger Suporte"
                  className="w-full"
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Se não informado, usaremos "Facebook Messenger"
                </p>
              </div>

              {/* Info Box about Facebook Login */}
              <div
                className="p-4 rounded-lg"
                style={{
                  backgroundColor: 'var(--theme-info-background)',
                  border: '1px solid var(--theme-info-color)',
                }}
              >
                <p
                  className="text-sm mb-3"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  <strong>Como funciona:</strong>
                </p>
                <ol
                  className="text-sm space-y-2 list-decimal list-inside"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  <li>Clique em "Conectar com Facebook"</li>
                  <li>Faça login com sua conta do Facebook</li>
                  <li>Selecione a Página do Facebook para receber mensagens</li>
                  <li>Autorize as permissões solicitadas</li>
                  <li>A configuração será feita automaticamente</li>
                </ol>
              </div>

              {/* Embedded Error */}
              {embeddedError && (
                <div
                  className="p-3 rounded-lg flex items-start gap-2"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                >
                  <Icon name="alert-circle" size={18}
                    style={{ color: 'var(--theme-error-color)' }}
                    className="flex-shrink-0 mt-0.5" />
                  <p
                    className="text-sm"
                    style={{ color: 'var(--theme-error-color)' }}
                  >
                    {embeddedError}
                  </p>
                </div>
              )}

              {/* General Error */}
              {errorMessage && (
                <div
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                >
                  <p
                    className="text-sm"
                    style={{ color: 'var(--theme-error-color)' }}
                  >
                    {errorMessage}
                  </p>
                </div>
              )}

              {/* Facebook Login Button */}
              <Button
                onClick={handleEmbeddedConnect}
                disabled={isEmbeddedLoading}
                className="w-full flex items-center justify-center gap-2"
                style={{
                  backgroundColor: '#1877F2',
                  color: 'white',
                }}
              >
                <Icon name="facebook" size={20} />
                {isEmbeddedLoading ? 'Conectando...' : 'Conectar com Facebook'}
              </Button>

              <p
                className="text-xs text-center"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                Método recomendado - configuração automática
              </p>
            </div>
          )}

          {/* ============================================= */}
          {/* MANUAL MODE - Credenciais Manuais            */}
          {/* ============================================= */}
          {authMode === 'manual' && (
            <div className="space-y-4">
              {/* Nome do Canal (opcional, auto-preenchido após selecionar página) */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Nome do Canal
                </label>
                <Input
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="Ex: Facebook Messenger Suporte"
                  className="w-full"
                  data-testid="messenger-form-channel-name"
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Se não informado, será gerado a partir do nome da página
                </p>
              </div>

              {/* Access Token + botão Conectar grudado à direita */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Access Token *
                </label>
                <div className="flex items-stretch gap-0">
                  <div className="relative flex-1">
                    <Icon name="key" size={18}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                      style={{ color: 'var(--theme-card-muted)' }} />
                    <Input
                      type="password"
                      value={accessToken}
                      onChange={(e) => {
                        setAccessToken(e.target.value);
                        if (pages.length > 0) resetManualDiscovery();
                      }}
                      placeholder="EAAxxxxxxxxxxxxxxx"
                      className="w-full pl-10"
                      style={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        borderRight: 'none',
                      }}
                      data-testid="messenger-form-access-token"
                    />
                  </div>
                  <Button
                    onClick={handleConnectToken}
                    disabled={isConnectingToken || !accessToken.trim()}
                    className="flex-shrink-0"
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      height: '36px',
                    }}
                    data-testid="messenger-form-connect-token-button"
                  >
                    {isConnectingToken ? (
                      <>
                        <Icon name="loader" size={14} className="mr-1.5 animate-spin" />
                        Conectando...
                      </>
                    ) : pages.length > 0 ? (
                      <>
                        <Icon name="refresh" size={14} className="mr-1.5" />
                        Reconectar
                      </>
                    ) : (
                      <>Conectar</>
                    )}
                  </Button>
                </div>
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Cole o token gerado no <a
                    href="https://developers.facebook.com/tools/explorer/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ color: 'var(--theme-info-color)' }}
                  >Graph API Explorer</a> e clique em Conectar
                </p>

                {tokenError && (
                  <div
                    className="mt-2 p-2.5 rounded-lg flex items-start gap-2"
                    style={{
                      backgroundColor: 'var(--theme-error-background)',
                      border: '1px solid var(--theme-error-color)',
                    }}
                    data-testid="messenger-form-token-error"
                  >
                    <Icon name="alert-circle" size={16}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: 'var(--theme-error-color)' }} />
                    <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
                      {tokenError}
                    </p>
                  </div>
                )}
              </div>

              {/* Alerta de expiração / scopes faltantes */}
              {tokenInfo && (
                <TokenStatusAlert
                  info={tokenInfo}
                  testIdPrefix="messenger-form"
                  accessToken={accessToken}
                />
              )}

              {/* Page select — visível após Conectar com sucesso */}
              {pages.length > 0 && (
                <Select
                  label="Página do Facebook (Page ID)"
                  required
                  options={pages.map((p) => ({
                    value: p.id,
                    label: `${p.name}${p.category ? ` · ${p.category}` : ''} (${p.id})`,
                  }))}
                  value={pageId}
                  onChange={handleSelectPage}
                  placeholder="Selecione a página para conectar"
                  data-testid="messenger-form-page-select"
                />
              )}

              {errorMessage && (
                <div
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                  data-testid="messenger-form-error"
                >
                  <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
                    {errorMessage}
                  </p>
                </div>
              )}

              <Button
                onClick={handleManualConnect}
                className="w-full"
                disabled={!pageId}
                data-testid="messenger-form-submit"
              >
                {!pageId ? 'Selecione uma página para continuar' : 'Conectar Messenger'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Connecting */}
      {step === 'connecting' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#1877F2' }}
            >
              <Icon name="loader" size={32} className="text-white animate-spin" />
            </div>
          </div>
          <p
            className="text-lg font-medium"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Conectando ao Facebook...
          </p>
          <p
            className="text-sm mt-2"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Aguarde enquanto configuramos seu canal
          </p>
        </div>
      )}

      {/* Step 3: Page Selection (Embedded mode only) */}
      {step === 'page-selection' && (
        <div className="space-y-4">
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-info-background)',
              border: '1px solid var(--theme-info-color)',
            }}
          >
            <p
              className="text-sm font-medium"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Selecione a Página do Facebook
            </p>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Escolha qual página receberá as mensagens do Messenger
            </p>
          </div>

          <div className="space-y-2">
            {availablePages.map((page) => (
              <button
                key={page.id}
                onClick={() => handlePageSelect(page)}
                className="w-full p-4 rounded-lg border-2 text-left flex items-center gap-3 transition-colors"
                style={{
                  borderColor: selectedPage?.id === page.id
                    ? 'var(--theme-button-primary)'
                    : 'var(--theme-card-border)',
                  backgroundColor: selectedPage?.id === page.id
                    ? 'var(--theme-button-primary-light)'
                    : 'var(--theme-card-background)',
                }}
                disabled={isEmbeddedLoading}
              >
                {page.picture ? (
                  <img
                    src={page.picture}
                    alt={page.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#1877F2' }}
                  >
                    <Icon name="facebook" size={24} className="text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p
                    className="font-medium truncate"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {page.name}
                  </p>
                  {page.category && (
                    <p
                      className="text-sm truncate"
                      style={{ color: 'var(--theme-card-muted)' }}
                    >
                      {page.category}
                    </p>
                  )}
                </div>
                {selectedPage?.id === page.id && (
                  <Icon name="check-circle" size={20}
                    style={{ color: 'var(--theme-success-color)' }} />
                )}
              </button>
            ))}
          </div>

          {isEmbeddedLoading && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Icon name="loader" size={20} className="animate-spin" style={{ color: 'var(--theme-card-muted)' }} />
              <span style={{ color: 'var(--theme-card-muted)' }}>Configurando...</span>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Show Webhook URL */}
      {step === 'webhook' && (
        <div className="space-y-4">
          <div
            className="p-4 rounded-lg flex items-start gap-3"
            style={{
              backgroundColor: 'var(--theme-success-background)',
              border: '1px solid var(--theme-success-color)',
            }}
          >
            <Icon name="check-circle" size={20}
              style={{ color: 'var(--theme-success-color)' }}
              className="flex-shrink-0 mt-0.5" />
            <div>
              <p
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Credenciais validadas com sucesso!
              </p>
              <p
                className="text-sm mt-1"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                Agora configure o webhook no Meta Business
              </p>
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              URL do Webhook
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={webhookUrl}
                readOnly
                className="flex-1"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  color: 'var(--theme-card-muted)',
                }}
              />
              <Button
                onClick={handleCopyWebhook}
                variant="secondary"
                className="flex-shrink-0"
              >
                <Icon name="copy" size={16} />
              </Button>
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Copie esta URL e configure no Meta for Developers
            </p>
          </div>

          <div
            className="p-4 rounded-lg space-y-2"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
            }}
          >
            <p
              className="text-sm font-medium"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Como configurar o webhook:
            </p>
            <ol
              className="text-sm space-y-1 list-decimal list-inside"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              <li>Acesse o Meta for Developers</li>
              <li>Vá no seu App &gt; Messenger &gt; Webhooks</li>
              <li>Cole a URL do webhook acima</li>
              <li>Configure o token de verificação (se solicitado)</li>
              <li>Inscreva-se nos eventos de mensagem</li>
            </ol>
            <a
              href="https://developers.facebook.com/docs/messenger-platform/webhooks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm flex items-center gap-1 hover:underline mt-2"
              style={{ color: 'var(--theme-info-color)' }}
            >
              Ver documentação completa
              <Icon name="external-link" size={14} />
            </a>
          </div>

          <Button
            onClick={handleComplete}
            className="w-full"
            data-testid="messenger-form-complete-button-webhook"
          >
            Concluir Configuração
          </Button>
        </div>
      )}

      {/* Step 5: Complete */}
      {step === 'complete' && (
        <div className="space-y-4">
          <div
            className="p-4 rounded-lg flex items-start gap-3"
            style={{
              backgroundColor: 'var(--theme-success-background)',
              border: '1px solid var(--theme-success-color)',
            }}
          >
            <Icon name="check-circle" size={20}
              style={{ color: 'var(--theme-success-color)' }}
              className="flex-shrink-0 mt-0.5" />
            <div>
              <p
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Facebook Messenger conectado com sucesso!
              </p>
              <p
                className="text-sm mt-1"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                Webhook configurado automaticamente. Você já pode receber mensagens!
              </p>
            </div>
          </div>

          {selectedPage && (
            <div
              className="p-4 rounded-lg flex items-center gap-3"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                border: '1px solid var(--theme-card-border)',
              }}
            >
              {selectedPage.picture ? (
                <img
                  src={selectedPage.picture}
                  alt={selectedPage.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#1877F2' }}
                >
                  <Icon name="facebook" size={24} className="text-white" />
                </div>
              )}
              <div>
                <p
                  className="font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {selectedPage.name}
                </p>
                <p
                  className="text-sm"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Página conectada
                </p>
              </div>
            </div>
          )}

          <Button
            onClick={handleComplete}
            className="w-full"
            data-testid="messenger-form-complete-button-complete"
          >
            Concluir Configuração
          </Button>
        </div>
      )}
    </div>
  );
};

FacebookMessengerForm.displayName = 'FacebookMessengerForm';
